import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import Image from "@tiptap/extension-image";
import YouTube from "@tiptap/extension-youtube";
import TextStyle from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import { Color } from "@tiptap/extension-color";
import Bold from "@tiptap/extension-bold";
import Heading from "@tiptap/extension-heading";
import Button from "../../layout/button";
import {Dialog,DialogBackdrop,DialogPanel,DialogTitle,
} from "@headlessui/react";
import { Auth } from "aws-amplify";

import { updateSurveyFromAPI } from "../../../services/surveyService";
import { updateTabStates } from "../../../store/actions";

export default function CollectEmailTemplate() {
  const themeMode = useSelector((state) => state.themeMode);
  // const currentSurvey = useSelector(state => state.currentSurvey);
  const topMenuTabs = useSelector((state) => state.topMenuTabs);

  // const themeMode = topMenuTabs.tabs.find((tab) => tab.id === topMenuTabs.activeTab).states.themeMode;
  const currentSurvey = topMenuTabs.tabs.find(
    (tab) => tab.id === topMenuTabs.activeTab
  ).states.currentSurvey;

  const dispatch = useDispatch();

  const [sendInfoMenuShow, setSendInfoMenuShow] = useState(false);
  let [token, setToken] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");

  async function checkAuth() {
    // window.location.reload();
    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const currentSession = await Auth.currentSession();
      await cognitoUser.refreshSession(
        currentSession.refreshToken,
        (err, session) => {
          const { idToken, refreshToken, accessToken } = session;
          getToken(idToken.jwtToken);
        }
      );
    } catch (e) {
      // console.log('Unable to refresh Token', e);
    }
  }

  useEffect(() => {
    getToken();
  }, []);

  async function getToken(token) {
    if (token) {
      setToken(token);
    } else {
      const currentSession = await Auth.currentSession();
      setToken(currentSession.idToken.jwtToken);
    }
  }

  useEffect(() => {
    setEmailSubject(currentSurvey?.email_template?.subject);
    setEmailMessage(currentSurvey?.email_template?.message);
  }, [currentSurvey]);

  useEffect(() => {
    // Custom Extensions for Font Size and Bold Mark
    const FontSizeTextStyle = TextStyle.extend({
      addAttributes() {
        return {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return { style: "font-size: " + attributes.fontSize };
            },
          },
        };
      },
    });

    const CustomBold = Bold.extend({
      renderHTML({ mark, HTMLAttributes }) {
        const { style, ...rest } = HTMLAttributes;
        const newStyle = "font-weight: bold;" + (style ? " " + style : "");
        return ["span", { ...rest, style: newStyle.trim() }, 0];
      },
      addOptions() {
        return {
          ...this.parent?.(),
          HTMLAttributes: {},
        };
      },
    });

    // Initialize the editor
    const editor = new Editor({
      element: document.querySelector("#wysiwyg-editor"),
      extensions: [
        StarterKit.configure({
          blockquote: false,
          marks: {
            bold: false,
          },
          heading: {
            levels: [1, 2, 3, 4, 5, 6], // Desteklenen heading seviyeleri
          },
        }),
        CustomBold,
        TextStyle,
        Color,
        FontSizeTextStyle,
        FontFamily,
        Highlight,
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
          defaultProtocol: "https",
        }),
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
        Heading.configure({
          levels: [1, 2, 3, 4, 5, 6], // Başlık seviyeleri burada tanımlanır
        }),
        HorizontalRule,
        Image,
        YouTube,
      ],
      content:
        emailMessage.length > 0
          ? emailMessage
          : currentSurvey?.email_template?.message,
      editorProps: {
        attributes: {
          class:
            "format lg:format-lg dark:format-invert focus:outline-none format-blue max-w-none",
        },
      },
      onUpdate({ editor }) {
        const updatedContent = editor.getHTML(); // Editörün güncel içeriğini al
        setEmailMessage(updatedContent); // Güncellenen içeriği kaydet
      },
    });

    // Set up event listeners for toolbar buttons (optional)
    const toggleBoldButton = document.getElementById("toggleBoldButton");
    document
      .getElementById("toggleBoldButton")
      .addEventListener("click", () =>
        editor.chain().focus().toggleBold().run()
      );
    document
      .getElementById("toggleItalicButton")
      .addEventListener("click", () =>
        editor.chain().focus().toggleItalic().run()
      );
    document
      .getElementById("toggleUnderlineButton")
      .addEventListener("click", () =>
        editor.chain().focus().toggleUnderline().run()
      );
    document
      .getElementById("toggleStrikeButton")
      .addEventListener("click", () =>
        editor.chain().focus().toggleStrike().run()
      );
    document
      .getElementById("toggleHighlightButton")
      .addEventListener("click", () => {
        const isHighlighted = editor.isActive("highlight");
        // when using toggleHighlight(), judge if is is already highlighted.
        editor
          .chain()
          .focus()
          .toggleHighlight({
            color: isHighlighted ? undefined : "#ffc078", // if is already highlighted，unset the highlight color
          })
          .run();
      });

    document
      .getElementById("toggleLinkButton")
      .addEventListener("click", () => {
        const url = window.prompt(
          "If you want to give the survey link, you should set it as [Survey Url].",
          "[Survey Url]"
        );
        editor.chain().focus().toggleLink({ href: url }).run();
      });
    document
      .getElementById("removeLinkButton")
      .addEventListener("click", () => {
        editor.chain().focus().unsetLink().run();
      });
    document
      .getElementById("toggleCodeButton")
      .addEventListener("click", () => {
        editor.chain().focus().toggleCode().run();
      });

    document
      .getElementById("toggleLeftAlignButton")
      .addEventListener("click", () => {
        editor.chain().focus().setTextAlign("left").run();
      });
    document
      .getElementById("toggleCenterAlignButton")
      .addEventListener("click", () => {
        editor.chain().focus().setTextAlign("center").run();
      });
    document
      .getElementById("toggleRightAlignButton")
      .addEventListener("click", () => {
        editor.chain().focus().setTextAlign("right").run();
      });
    const toggleButton = document.getElementById("toggleParagraphButton");
    const textSizeDropdown = document.getElementById("textSizeDropdown");
    const textSizeDropdownButton = document.getElementById(
      "toggleTextSizeButton"
    );
    const textColorDropdownButton = document.getElementById(
      "toggleTextColorButton"
    );
    const textColorDropdown = document.getElementById("textColorDropdown");
    const fontFamilyDropdown = document.getElementById("fontFamilyDropdown");
    const fontFamilyDropdownButton = document.getElementById(
      "toggleFontFamilyButton"
    );

    // Loop through all elements with the data-text-size attribute
    document.querySelectorAll("[data-text-size]").forEach((button) => {
      button.addEventListener("click", () => {
        const fontSize = button.getAttribute("data-text-size");

        // Apply the selected font size via pixels using the TipTap editor chain
        editor.chain().focus().setMark("textStyle", { fontSize }).run();

        // Hide the dropdown after selection
        toggleTextSizeDropdown();
      });
    });
    
    const toggleTextSizeDropdown = () => {
      if (textSizeDropdown.classList.contains("hidden")) {
        textSizeDropdown.classList.remove("hidden");
        textColorDropdown.classList.add("hidden");
        fontFamilyDropdown.classList.add("hidden");
      } else {
        textSizeDropdown.classList.add("hidden");
      }
    };
    textSizeDropdownButton.addEventListener("click", () => {
      // Burada editor.chain().focus().setParagraph().run(); ile editor'ün paragraf stilini değiştirebilirsiniz.

      toggleTextSizeDropdown(); // Dropdown göster/gizle işlemi
    });
    const colorPicker = document.getElementById("color");
    colorPicker.addEventListener("input", (event) => {
      const selectedColor = event.target.value;

      // Apply the selected color to the selected text
      editor.chain().focus().setColor(selectedColor).run();
    });

    document.querySelectorAll("[data-hex-color]").forEach((button) => {
      button.addEventListener("click", () => {
        const selectedColor = button.getAttribute("data-hex-color");

        // Apply the selected color to the selected text
        editor.chain().focus().setColor(selectedColor).run();
      });
    });

    document.getElementById("reset-color").addEventListener("click", () => {
      editor.commands.unsetColor();
    });
    const toggleTextColorDropdown = () => {
      if (textColorDropdown.classList.contains("hidden")) {
        textColorDropdown.classList.remove("hidden");
        textSizeDropdown.classList.add("hidden");
        fontFamilyDropdown.classList.add("hidden");
      } else {
        textColorDropdown.classList.add("hidden");
      }
    };
    textColorDropdownButton.addEventListener("click", () => {
      // Burada editor.chain().focus().setParagraph().run(); ile editor'ün paragraf stilini değiştirebilirsiniz.

      toggleTextColorDropdown(); // Dropdown göster/gizle işlemi
    });
    // Loop through all elements with the data-font-family attribute
    document.querySelectorAll("[data-font-family]").forEach((button) => {
      button.addEventListener("click", () => {
        const fontFamily = button.getAttribute("data-font-family");

        // Apply the selected font size via pixels using the TipTap editor chain
        editor.chain().focus().setFontFamily(fontFamily).run();

        // Hide the dropdown after selection
        toggleFontFamilyDropdown();
      });
    });
    const toggleFontFamilyDropdown = () => {
      if (fontFamilyDropdown.classList.contains("hidden")) {
        fontFamilyDropdown.classList.remove("hidden");
        textColorDropdown.classList.add("hidden");
        textSizeDropdown.classList.add("hidden");
      } else {
        fontFamilyDropdown.classList.add("hidden");
      }
    };
    fontFamilyDropdownButton.addEventListener("click", () => {
      // Burada editor.chain().focus().setParagraph().run(); ile editor'ün paragraf stilini değiştirebilirsiniz.

      toggleFontFamilyDropdown(); // Dropdown göster/gizle işlemi
    });
    return () => {
      // Cleanup on component unmount
      editor.destroy();
    };
  }, []);

  async function emailTemplateSubmit() {
    dispatch(updateTabStates(topMenuTabs.activeTab, 'pageIsLoading', true));
    const obj = {
      email_template: {
        subject: emailSubject,
        message: emailMessage,
      },
    };

    const _currentSurvey = { ...currentSurvey };
    _currentSurvey.email_template = obj.email_template;

    console.log("update survey obj : ", obj);

    await updateSurveyFromAPI(token, obj, currentSurvey.ID)
      .then((result) => {
        console.log("update survey result : ", result);
        dispatch(
          updateTabStates(
            topMenuTabs.activeTab,
            "currentSurvey",
            _currentSurvey
          )
        );
        setSendInfoMenuShow(true);
      }).catch((error) => {
        console.log("update survey error : ", error);
        setSendInfoMenuShow(false);
      }).finally(() => {
        dispatch(updateTabStates(topMenuTabs.activeTab, 'pageIsLoading', false));
      });
  }
  useEffect(() => {
    const timer = setTimeout(() => {
      setSendInfoMenuShow(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [sendInfoMenuShow, setSendInfoMenuShow]);
  return (
    <div
      className={` h-full w-full justify-between space-y-4 ${
        themeMode === "dark" ? "bg-[#1a1a28]" : "bg-[#fbfbfc]"
      } `}
    >
      {/* <div
                className={`flex flex-grow  flex flex-col items-center justify-center p-6 space-y-6 h-full grid grid-cols-12" ${themeMode === "dark" ? "bg-[#1a1a28]" : "bg-[#ffffff]"
                    }`}
            > */}
      <div className="flex justify-start px-12 pt-4 flex-col space-y-2 sm:flex-row sm:space-y-0 w-full xl:w-1/2">
        <span
          className={`w-1/6 font-bold py-1  leading-4 text-base whitespace-nowrap break-all  min-w-0  ${
            themeMode === "dark" ? "text-[#c1c9d6]" : "text-[#5a667e]"
          }`}
        >
          Subject:
        </span>
        <div
          className={`w-5/6 rounded-[0.375rem] px-3  py-1.5 border w-1/2  ${
            themeMode === "dark"
              ? "bg-[#1C1C29] border-[1px] border-solid border-[rgba(255,255,255,0.09)]"
              : "border-[1px] border-solid  bg-[#F4F4F6]"
          }  `}
        >
          <input
            id="name"
            name="name"
            type="text"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            className={`block w-full sm:text-sm/6 ${
              themeMode === "dark"
                ? "bg-[#1C1C29] border-gray-600 placeholder:text-white text-white "
                : "border-gray-200 bg-[#F4F4F6] text-[#0f121a] placeholder:text-[#0f121a]"
            }`}
          />
        </div>
      </div>
      <div className="flex justify-start px-12 flex-col space-y-2 sm:flex-row sm:space-y-0 w-full xl:w-1/2">
        <span
          className={`w-1/6 font-bold py-1  leading-4 text-base whitespace-nowrap break-all  min-w-0  ${
            themeMode === "dark" ? "text-[#c1c9d6]" : "text-[#5a667e]"
          }`}
        >
          Message:
        </span>

        <div
          className={`w-5/6 border rounded-lg ${
            themeMode === "dark"
              ? "bg-[#1C1C29] border-[1px] border-solid border-[rgba(255,255,255,0.09)]"
              : "border-[1px] border-solid  bg-[#F4F4F6]"
          }`}
        >
          <div
            className={`px-3 py-2 border-b border-t-0 boorder-r-0 border-l-0 ${
              themeMode === "dark"
                ? "bg-[#1C1C29] border-[1px] border-solid border-[rgba(255,255,255,0.09)]"
                : "border-[1px] border-solid  bg-[#F4F4F6]"
            }`}
          >
            <div className="flex flex-wrap items-center">
              <div className="flex items-center space-x-1 rtl:space-x-reverse flex-wrap">
                <button
                  id="toggleBoldButton"
                  data-tooltip-target="tooltip-bold"
                  type="button"
                  className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 5h4.5a3.5 3.5 0 1 1 0 7H8m0-7v7m0-7H6m2 7h6.5a3.5 3.5 0 1 1 0 7H8m0-7v7m0 0H6"
                    />
                  </svg>
                  <span className="sr-only">Bold</span>
                </button>
                <div
                  id="tooltip-bold"
                  role="tooltip"
                  className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                >
                  Toggle bold
                  <div className="tooltip-arrow" data-popper-arrow></div>
                </div>
                <button
                  id="toggleItalicButton"
                  data-tooltip-target="tooltip-italic"
                  type="button"
                  className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m8.874 19 6.143-14M6 19h6.33m-.66-14H18"
                    />
                  </svg>
                  <span className="sr-only">Italic</span>
                </button>
                <div
                  id="tooltip-italic"
                  role="tooltip"
                  className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                >
                  Toggle italic
                  <div className="tooltip-arrow" data-popper-arrow></div>
                </div>
                <button
                  id="toggleUnderlineButton"
                  data-tooltip-target="tooltip-underline"
                  type="button"
                  className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeWidth="2"
                      d="M6 19h12M8 5v9a4 4 0 0 0 8 0V5M6 5h4m4 0h4"
                    />
                  </svg>
                  <span className="sr-only">Underline</span>
                </button>
                <div
                  id="tooltip-underline"
                  role="tooltip"
                  className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                >
                  Toggle underline
                  <div className="tooltip-arrow" data-popper-arrow></div>
                </div>
                <button
                  id="toggleStrikeButton"
                  data-tooltip-target="tooltip-strike"
                  type="button"
                  className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M7 6.2V5h12v1.2M7 19h6m.2-14-1.677 6.523M9.6 19l1.029-4M5 5l6.523 6.523M19 19l-7.477-7.477"
                    />
                  </svg>
                  <span className="sr-only">Strike</span>
                </button>
                <div
                  id="tooltip-strike"
                  role="tooltip"
                  className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                >
                  Toggle strike
                  <div className="tooltip-arrow" data-popper-arrow></div>
                </div>
                <button
                  id="toggleHighlightButton"
                  data-tooltip-target="tooltip-highlight"
                  type="button"
                  className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeWidth="2"
                      d="M9 19.2H5.5c-.3 0-.5-.2-.5-.5V16c0-.2.2-.4.5-.4h13c.3 0 .5.2.5.4v2.7c0 .3-.2.5-.5.5H18m-6-1 1.4 1.8h.2l1.4-1.7m-7-5.4L12 4c0-.1 0-.1 0 0l4 8.8m-6-2.7h4m-7 2.7h2.5m5 0H17"
                    />
                  </svg>
                  <span className="sr-only">Highlight</span>
                </button>
                <div
                  id="tooltip-highlight"
                  role="tooltip"
                  className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                >
                  Toggle highlight
                  <div className="tooltip-arrow" data-popper-arrow></div>
                </div>
                <button
                  id="toggleCodeButton"
                  type="button"
                  data-tooltip-target="tooltip-code"
                  className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m8 8-4 4 4 4m8 0 4-4-4-4m-2-3-4 14"
                    />
                  </svg>
                  <span className="sr-only">Code</span>
                </button>
                <div
                  id="tooltip-code"
                  role="tooltip"
                  className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                >
                  Format code
                  <div className="tooltip-arrow" data-popper-arrow></div>
                </div>
                <button
                  id="toggleLinkButton"
                  data-tooltip-target="tooltip-link"
                  type="button"
                  className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13.213 9.787a3.391 3.391 0 0 0-4.795 0l-3.425 3.426a3.39 3.39 0 0 0 4.795 4.794l.321-.304m-.321-4.49a3.39 3.39 0 0 0 4.795 0l3.424-3.426a3.39 3.39 0 0 0-4.794-4.795l-1.028.961"
                    />
                  </svg>
                  <span className="sr-only">Link</span>
                </button>
                <div
                  id="tooltip-link"
                  role="tooltip"
                  className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                >
                  Add link
                  <div className="tooltip-arrow" data-popper-arrow></div>
                </div>
                <button
                  id="removeLinkButton"
                  data-tooltip-target="tooltip-remove-link"
                  type="button"
                  className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeWidth="2"
                      d="M13.2 9.8a3.4 3.4 0 0 0-4.8 0L5 13.2A3.4 3.4 0 0 0 9.8 18l.3-.3m-.3-4.5a3.4 3.4 0 0 0 4.8 0L18 9.8A3.4 3.4 0 0 0 13.2 5l-1 1m7.4 14-1.8-1.8m0 0L16 16.4m1.8 1.8 1.8-1.8m-1.8 1.8L16 20"
                    />
                  </svg>
                  <span className="sr-only">Remove link</span>
                </button>
                <div
                  id="tooltip-remove-link"
                  role="tooltip"
                  className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                >
                  Remove link
                  <div className="tooltip-arrow" data-popper-arrow></div>
                </div>
                <div className="relative">
                  <button
                    id="toggleTextSizeButton"
                    data-dropdown-toggle="textSizeDropdown"
                    type="button"
                    data-tooltip-target="tooltip-text-size"
                    className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                  >
                    <svg
                      className="w-5 h-5"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 6.2V5h11v1.2M8 5v14m-3 0h6m2-6.8V11h8v1.2M17 11v8m-1.5 0h3"
                      />
                    </svg>
                    <span className="sr-only">Text size</span>
                  </button>
                  <div
                    id="tooltip-text-size"
                    role="tooltip"
                    className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                  >
                    Text size
                    <div className="tooltip-arrow" data-popper-arrow></div>
                  </div>
                  <div
                    id="textSizeDropdown"
                    className={`z-10 absolute top-10 hidden w-72 rounded ${
                      themeMode === "dark"
                        ? " bg-[#2a2939] border-white-09 shadow-theme-popup-shadow "
                        : "bg-[#FFFFFF] border-black-09 shadow-black-2-shadow"
                    } p-2 shadow `}
                  >
                    <ul
                      className="space-y-1 text-sm font-medium"
                      aria-labelledby="toggleTextSizeButton"
                    >
                      <li>
                        <button
                          data-text-size="16px"
                          type="button"
                          className={`flex justify-between items-center w-full text-base rounded px-3 py-2  ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          }`}
                        >
                          16px (Default)
                        </button>
                      </li>
                      <li>
                        <button
                          data-text-size="12px"
                          type="button"
                          className={`flex justify-between items-center w-full text-xs rounded px-3 py-2  ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          }`}
                        >
                          12px (Tiny)
                        </button>
                      </li>
                      <li>
                        <button
                          data-text-size="14px"
                          type="button"
                          className={`flex justify-between items-center w-full text-sm rounded px-3 py-2  ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          }`}
                        >
                          14px (Small)
                        </button>
                      </li>
                      <li>
                        <button
                          data-text-size="18px"
                          type="button"
                          className={`flex justify-between items-center w-full text-lg rounded px-3 py-2  ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          }`}
                        >
                          18px (Lead)
                        </button>
                      </li>
                      <li>
                        <button
                          data-text-size="24px"
                          type="button"
                          className={`flex justify-between items-center w-full text-2xl rounded px-3 py-2  ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          }`}
                        >
                          24px (Large)
                        </button>
                      </li>
                      <li>
                        <button
                          data-text-size="36px"
                          type="button"
                          className={`flex justify-between items-center w-full text-4xl rounded px-3 py-2  ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          }`}
                        >
                          36px (Huge)
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="relative">
                  <button
                    id="toggleTextColorButton"
                    data-dropdown-toggle="textColorDropdown"
                    type="button"
                    data-tooltip-target="tooltip-text-color"
                    className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                  >
                    <svg
                      className="w-5 h-5"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="25"
                      height="24"
                      fill="none"
                      viewBox="0 0 25 24"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeWidth="2"
                        d="m6.532 15.982 1.573-4m-1.573 4h-1.1m1.1 0h1.65m-.077-4 2.725-6.93a.11.11 0 0 1 .204 0l2.725 6.93m-5.654 0H8.1m.006 0h5.654m0 0 .617 1.569m5.11 4.453c0 1.102-.854 1.996-1.908 1.996-1.053 0-1.907-.894-1.907-1.996 0-1.103 1.907-4.128 1.907-4.128s1.909 3.025 1.909 4.128Z"
                      />
                    </svg>
                    <span className="sr-only">Text color</span>
                  </button>
                  <div
                    id="tooltip-text-color"
                    role="tooltip"
                    className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                  >
                    Text color
                    <div className="tooltip-arrow" data-popper-arrow></div>
                  </div>
                  <div
                    id="textColorDropdown"
                    className={`z-10 absolute top-10 hidden w-48 rounded p-2 ${
                      themeMode === "dark"
                        ? " bg-[#2a2939] border-white-09 shadow-theme-popup-shadow "
                        : "bg-[#FFFFFF] border-black-09 shadow-black-2-shadow"
                    } `}
                  >
                    <div
                      className={`grid grid-cols-6 gap-2 group mb-3 items-center p-1.5 rounded-lg {
                        themeMode === "dark"
                          ? "hover:bg-[#333240] text-white"
                          : "hover:bg-[#EBEBEB] text-black"
                      }`}
                    >
                      <input
                        type="color"
                        id="color"
                        value="#e66465"
                        className="border-gray-200 border  dark:border-gray-600 rounded-md p-px px-1 w-full h-8 col-span-3"
                      />
                      <label
                        for="color"
                        className={`text-sm font-medium col-span-3 ${
                          themeMode === "dark" ? "text-white" : "text-black"
                        }`}
                      >
                        Pick a color
                      </label>
                    </div>
                    <div className="grid grid-cols-6 gap-1 mb-3">
                      <button
                        type="button"
                        data-hex-color="#1A56DB"
                        className="w-6 h-6 rounded-md bg-[#1A56DB]"
                      >
                        <span className="sr-only">Blue</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#0E9F6E"
                        className="w-6 h-6 rounded-md bg-[#0E9F6E]"
                      >
                        <span className="sr-only">Green</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#FACA15"
                        className="w-6 h-6 rounded-md bg-[#FACA15]"
                      >
                        <span className="sr-only">Yellow</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#F05252"
                        className="w-6 h-6 rounded-md bg-[#F05252]"
                      >
                        <span className="sr-only">Red</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#FF8A4C"
                        className="w-6 h-6 rounded-md bg-[#FF8A4C]"
                      >
                        <span className="sr-only">Orange</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#0694A2"
                        className="w-6 h-6 rounded-md bg-[#0694A2]"
                      >
                        <span className="sr-only">Teal</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#B4C6FC"
                        className="w-6 h-6 rounded-md bg-[#B4C6FC]"
                      >
                        <span className="sr-only">Light indigo</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#8DA2FB"
                        className="w-6 h-6 rounded-md bg-[#8DA2FB]"
                      >
                        <span className="sr-only">Indigo</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#5145CD"
                        className="w-6 h-6 rounded-md bg-[#5145CD]"
                      >
                        <span className="sr-only">Purple</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#771D1D"
                        className="w-6 h-6 rounded-md bg-[#771D1D]"
                      >
                        <span className="sr-only">Brown</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#FCD9BD"
                        className="w-6 h-6 rounded-md bg-[#FCD9BD]"
                      >
                        <span className="sr-only">Light orange</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#99154B"
                        className="w-6 h-6 rounded-md bg-[#99154B]"
                      >
                        <span className="sr-only">Bordo</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#7E3AF2"
                        className="w-6 h-6 rounded-md bg-[#7E3AF2]"
                      >
                        <span className="sr-only">Dark Purple</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#CABFFD"
                        className="w-6 h-6 rounded-md bg-[#CABFFD]"
                      >
                        <span className="sr-only">Light</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#D61F69"
                        className="w-6 h-6 rounded-md bg-[#D61F69]"
                      >
                        <span className="sr-only">Dark Pink</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#F8B4D9"
                        className="w-6 h-6 rounded-md bg-[#F8B4D9]"
                      >
                        <span className="sr-only">Pink</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#F6C196"
                        className="w-6 h-6 rounded-md bg-[#F6C196]"
                      >
                        <span className="sr-only">Cream</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#A4CAFE"
                        className="w-6 h-6 rounded-md bg-[#A4CAFE]"
                      >
                        <span className="sr-only">Light Blue</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#5145CD"
                        className="w-6 h-6 rounded-md bg-[#5145CD]"
                      >
                        <span className="sr-only">Dark Blue</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#B43403"
                        className="w-6 h-6 rounded-md bg-[#B43403]"
                      >
                        <span className="sr-only">Orange Brown</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#FCE96A"
                        className="w-6 h-6 rounded-md bg-[#FCE96A]"
                      >
                        <span className="sr-only">Light Yellow</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#1E429F"
                        className="w-6 h-6 rounded-md bg-[#1E429F]"
                      >
                        <span className="sr-only">Navy Blue</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#768FFD"
                        className="w-6 h-6 rounded-md bg-[#768FFD]"
                      >
                        <span className="sr-only">Light Purple</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#BCF0DA"
                        className="w-6 h-6 rounded-md bg-[#BCF0DA]"
                      >
                        <span className="sr-only">Light Green</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#EBF5FF"
                        className="w-6 h-6 rounded-md bg-[#EBF5FF]"
                      >
                        <span className="sr-only">Sky Blue</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#16BDCA"
                        className="w-6 h-6 rounded-md bg-[#16BDCA]"
                      >
                        <span className="sr-only">Cyan</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#E74694"
                        className="w-6 h-6 rounded-md bg-[#E74694]"
                      >
                        <span className="sr-only">Pink</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#83B0ED"
                        className="w-6 h-6 rounded-md bg-[#83B0ED]"
                      >
                        <span className="sr-only">Darker Sky Blue</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#03543F"
                        className="w-6 h-6 rounded-md bg-[#03543F]"
                      >
                        <span className="sr-only">Forest Green</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#111928"
                        className="w-6 h-6 rounded-md bg-[#111928]"
                      >
                        <span className="sr-only">Black</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#4B5563"
                        className="w-6 h-6 rounded-md bg-[#4B5563]"
                      >
                        <span className="sr-only">Stone</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#6B7280"
                        className="w-6 h-6 rounded-md bg-[#6B7280]"
                      >
                        <span className="sr-only">Gray</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#D1D5DB"
                        className="w-6 h-6 rounded-md bg-[#D1D5DB]"
                      >
                        <span className="sr-only">Light Gray</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#F3F4F6"
                        className="w-6 h-6 rounded-md bg-[#F3F4F6]"
                      >
                        <span className="sr-only">Cloud Gray</span>
                      </button>
                      <button
                        type="button"
                        data-hex-color="#F9FAFB"
                        className="w-6 h-6 rounded-md bg-[#F9FAFB]"
                      >
                        <span className="sr-only">Heaven Gray</span>
                      </button>
                    </div>

                    <button
                      type="button"
                      id="reset-color"
                      className={`py-1.5 text-sm font-medium ${
                        themeMode === "dark"
                          ? "border-[#d1d5de0a] bg-[#d1d5de0d] hover:bg-[#d1d5de0a] text-white"
                          : "border-[#1725471a] bg-[#1725470d] hover:bg-[#1725471a] text-black"
                      }  focus:outline-none rounded-lg  w-full `}
                    >
                      Reset color
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <button
                    id="toggleFontFamilyButton"
                    data-dropdown-toggle="fontFamilyDropdown"
                    type="button"
                    data-tooltip-target="tooltip-font-family"
                    className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                  >
                    <svg
                      className="w-5 h-5"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="m10.6 19 4.298-10.93a.11.11 0 0 1 .204 0L19.4 19m-8.8 0H9.5m1.1 0h1.65m7.15 0h-1.65m1.65 0h1.1m-7.7-3.985h4.4M3.021 16l1.567-3.985m0 0L7.32 5.07a.11.11 0 0 1 .205 0l2.503 6.945h-5.44Z"
                      />
                    </svg>
                    <span className="sr-only">Font family</span>
                  </button>
                  <div
                    id="tooltip-font-family"
                    role="tooltip"
                    className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                  >
                    Font Family
                    <div className="tooltip-arrow" data-popper-arrow></div>
                  </div>
                  <div
                    id="fontFamilyDropdown"
                    className={`z-10 p-2 absolute top-10 hidden w-48 rounded  ${
                      themeMode === "dark"
                        ? " bg-[#2a2939] border-white-09 shadow-theme-popup-shadow "
                        : "bg-[#FFFFFF] border-black-09 shadow-black-2-shadow"
                    }`}
                  >
                    <ul
                      className="space-y-1 text-sm font-medium"
                      aria-labelledby="toggleFontFamilyButton"
                    >
                      <li>
                        <button
                          data-font-family="Inter, ui-sans-serif"
                          type="button"
                          className={`flex justify-between items-center w-full text-sm font-sans rounded px-3 py-2 ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          }`}
                        >
                          Default
                        </button>
                      </li>
                      <li>
                        <button
                          data-font-family="Arial, sans-serif"
                          type="button"
                          className={`flex justify-between items-center w-full text-sm font-sans rounded px-3 py-2 ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          } font-sans `}
                        >
                          Arial
                        </button>
                      </li>
                      <li>
                        <button
                          data-font-family="'Courier New', monospace"
                          type="button"
                          className={`flex justify-between items-center w-full text-sm font-sans rounded px-3 py-2 ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          } font-mono`}
                        >
                          Courier New
                        </button>
                      </li>
                      <li>
                        <button
                          data-font-family="Georgia, serif"
                          type="button"
                          className={`flex justify-between items-center w-full text-sm font-sans rounded px-3 py-2 ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          } font-serif`}
                        >
                          Georgia
                        </button>
                      </li>
                      <li>
                        <button
                          data-font-family="'Lucida Sans Unicode', sans-serif"
                          type="button"
                          className={`flex justify-between items-center w-full text-sm font-sans rounded px-3 py-2 ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          } font-lucida`}
                        >
                          Lucida Sans Unicode
                        </button>
                      </li>
                      <li>
                        <button
                          data-font-family="Tahoma, sans-serif"
                          type="button"
                          className={`flex justify-between items-center w-full text-sm font-sans rounded px-3 py-2 ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          } font-tahoma`}
                        >
                          Tahoma
                        </button>
                      </li>
                      <li>
                        <button
                          data-font-family="'Times New Roman', serif;"
                          type="button"
                          className={`flex justify-between items-center w-full text-sm font-sans rounded px-3 py-2 ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          } font-times`}
                        >
                          Times New Roman
                        </button>
                      </li>
                      <li>
                        <button
                          data-font-family="'Trebuchet MS', sans-serif"
                          type="button"
                          className={`flex justify-between items-center w-full text-sm font-sans rounded px-3 py-2 ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          } font-trebuchet`}
                        >
                          Trebuchet MS
                        </button>
                      </li>
                      <li>
                        <button
                          data-font-family="Verdana, sans-serif"
                          type="button"
                          className={`flex justify-between items-center w-full text-sm font-sans rounded px-3 py-2 ${
                            themeMode === "dark"
                              ? "hover:bg-[#333240] text-white"
                              : "hover:bg-[#EBEBEB] text-black"
                          } font-verdana`}
                        >
                          Verdana
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="px-1">
                  <span className="block w-px h-4 bg-gray-300 dark:bg-gray-600"></span>
                </div>
                <button
                  id="toggleLeftAlignButton"
                  type="button"
                  data-tooltip-target="tooltip-left-align"
                  className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 6h8m-8 4h12M6 14h8m-8 4h12"
                    />
                  </svg>
                  <span className="sr-only">Align left</span>
                </button>
                <div
                  id="tooltip-left-align"
                  role="tooltip"
                  className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                >
                  Align left
                  <div className="tooltip-arrow" data-popper-arrow></div>
                </div>
                <button
                  id="toggleCenterAlignButton"
                  type="button"
                  data-tooltip-target="tooltip-center-align"
                  className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 6h8M6 10h12M8 14h8M6 18h12"
                    />
                  </svg>
                  <span className="sr-only">Align center</span>
                </button>
                <div
                  id="tooltip-center-align"
                  role="tooltip"
                  className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                >
                  Align center
                  <div className="tooltip-arrow" data-popper-arrow></div>
                </div>
                <button
                  id="toggleRightAlignButton"
                  type="button"
                  data-tooltip-target="tooltip-right-align"
                  className="p-1.5 text-gray-500 rounded cursor-pointer hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M18 6h-8m8 4H6m12 4h-8m8 4H6"
                    />
                  </svg>
                  <span className="sr-only">Align right</span>
                </button>
                <div
                  id="tooltip-right-align"
                  role="tooltip"
                  className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700"
                >
                  Align right
                  <div className="tooltip-arrow" data-popper-arrow></div>
                </div>
              </div>
            </div>
          </div>
          <div className="px-4 py-2 bg-gary-800 rounded-b-lg dark:bg-gray-800">
            <div
              id="wysiwyg-editor"
              className={`edtior-container ${
                themeMode === "dark"
                  ? "text-white placeholder-gray-400"
                  : "text-gray-800"
              }`}
            ></div>
            <label for="wysiwyg-example" className="sr-only">
              Publish post
            </label>
            <div
              id="wysiwyg-example"
              className="block w-full px-0 text-sm text-gray-800 bg-white border-0 dark:bg-gray-800 focus:ring-0 dark:text-white dark:placeholder-gray-400"
            ></div>
          </div>
        </div>
      </div>
      <div className="flex justify-start cursor-pointer px-12 ">
        <div className="flex w-full xl:w-1/2 justify-start gap-2 mr-17">
         <Button text="Save" onClick={()=> emailTemplateSubmit()} disabled={false}  themeMode={themeMode}  className="flex-shrink-0 px-3 py-0 h-[1.5rem] lg:h-8   rounded-lg transition duration-150 min-w-[1.375rem] whitespace-nowrap text-[0.8125rem] flex items-center" type="submit"/>
         
        </div>
      </div>
      <Dialog
        open={sendInfoMenuShow}
        onClose={setSendInfoMenuShow}
        className="relative z-10"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0  transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto px-[6rem]">
          <div className=" absolute flex min-h-full items-end justify-start  p-4 text-center sm:p-0">
            <DialogPanel
              transition
              className={`relative transform  rounded-lg shadow-black-2-shadow border border-solid ${
                themeMode === "dark"
                  ? "bg-[#2a2939] border-white-09"
                  : "bg-[#FFFFFF] border-black-09 "
              } overflow-hidden rounded-lg   px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
            >
              <div>
                <div className="mt-0 text-start sm:mt-0">
                  <DialogTitle
                    as="h3"
                    className={`text-base font-semibold flex flex-row justify-start ${
                      themeMode === "dark" ? "text-[#fff]" : "text-[#000]"
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                        fill="#008000"
                        stroke="#fff"
                      />
                    </svg>
                    Mail Template saved
                  </DialogTitle>
                </div>
              </div>
              <div className="flex-shrink-0 grid grid-flow-col rtl justify-end items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setSendInfoMenuShow(false)}
                  className={`mt-3 inline-flex justify-center rounded-[0.375rem] border-solid bg-[#205dc2] hover:bg-[#3575de] hover:text-white text-white  ${
                    themeMode === "dark"
                      ? " border-white-09  "
                      : " border-black-09 "
                  } px-3 py-2 text-sm font-semibold shadow-sm sm:col-start-2 sm:mt-0`}
                >
                  Ok
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
      {/* </div> */}
      {/* Drawer */}
    </div>
  );
}
